/**
 * Доступность заблокированных в РФ ресурсов.
 *
 * Каждая цель — небольшая картинка по HTTPS. Загрузка идёт через <img>, а не
 * через fetch, по двум причинам:
 *   1. <img> не требует CORS: браузер не даст прочитать пиксели, но событие
 *      load/error мы получаем, а этого достаточно;
 *   2. fetch к заблокированному домену в некоторых сетях висит до таймаута,
 *      а <img> отваливается по error быстрее.
 *
 * Успех загрузки — надёжный сигнал: ресурс реально доступен. Неудача —
 * ненадёжный: ресурс мог лежать сам по себе. Поэтому сервер считает вывод по
 * доле открывшихся целей (analysis/blockedAccess.ts), а не по одной.
 *
 * Проверки идут с ограниченной параллельностью: десяток одновременных
 * запросов к заблокированным доменам в некоторых сетях приводит к тому, что
 * провайдер режет их пачкой, и результат смазывается.
 */

import type { BlockedTarget, BlockedTargetResult } from '@vpncheck/shared';

/** Сколько целей проверять одновременно. */
const CONCURRENCY = 3;

export type BlockedProgress = (done: number, total: number) => void;

export async function runBlockedChecks(
  targets: readonly BlockedTarget[],
  timeoutMs: number,
  onProgress?: BlockedProgress,
): Promise<BlockedTargetResult[]> {
  const results: BlockedTargetResult[] = [];
  const queue = [...targets];
  let done = 0;

  const workers = Array.from(
    { length: Math.min(CONCURRENCY, queue.length) },
    async () => {
      for (;;) {
        const target = queue.shift();
        if (!target) return;
        results.push(await probeTarget(target, timeoutMs));
        done += 1;
        onProgress?.(done, targets.length);
      }
    },
  );

  await Promise.all(workers);

  // Возвращаем в исходном порядке: так отчёт стабилен между запусками.
  const order = new Map(targets.map((t, i) => [t.id, i]));
  return results.sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
}

/** Пытается загрузить одну цель. Возвращает результат всегда, без исключений. */
function probeTarget(target: BlockedTarget, timeoutMs: number): Promise<BlockedTargetResult> {
  return new Promise((resolve) => {
    const started = performance.now();
    const img = new Image();
    let settled = false;

    const finish = (reachable: boolean, timedOut: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      // Обрываем загрузку: иначе картинка продолжит качаться в фоне и будет
      // мешать замеру следующих целей.
      img.src = '';
      resolve({
        id: target.id,
        reachable,
        elapsedMs: Math.round(performance.now() - started),
        timedOut,
      });
    };

    const timer = setTimeout(() => finish(false, true), timeoutMs);

    img.onload = () => finish(true, false);
    img.onerror = () => finish(false, false);
    // Referrer не отправляем: незачем сообщать заблокированному хосту, откуда
    // пришёл запрос, и это уменьшает шум в его логах.
    img.referrerPolicy = 'no-referrer';
    img.crossOrigin = null;
    // Уникальный параметр обходит кеш браузера: иначе повторная проверка
    // покажет успех по старой записи, даже если доступ уже закрыт.
    img.src = appendCacheBuster(target.url);
  });
}

function appendCacheBuster(url: string): string {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}_=${Date.now().toString(36)}`;
}
